/**
 * The enrichment editor: every editable key of an enrichment object, rendered
 * generically from the config plus the runtime value (design C.3, C.6).
 *
 * The prop shape is `{ config, enrichment, updateEnrichment }` on purpose — it
 * is the shape the Tina HITL panel already passes to `ArtistEnrichmentReview`
 * (`src/pages/tina/items/components/panels/hitl-panel/enrichment-resolution.tsx`),
 * so swapping that panel onto this component is an import change, not a
 * rewrite.
 *
 * Three key sets are NOT rendered here, and each for its own reason:
 *
 * - `translations` and `transliterations` are owned by their own sections
 *   (`NEVER_INLINE_KEYS`); the page composes those two around this editor;
 * - the pipeline-owned keys (`taskName`, `id`, `enrichmentSources`, …) ride
 *   along as `hidden` AutoForm fields, so the round trip keeps them;
 * - `reasoning` (D1) is read-only and gets a collapsible card of its own.
 *
 * The form's values are pushed up on every change, so the page's `draft` is
 * always the thing the curator sees. That is also why the editor pushes the RAW
 * react-hook-form values: they still carry `""` where a text input was emptied,
 * which is the only signal that a field must be CLEARED rather than left alone
 * (see `withClearedFields` in `@/lib/utils`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Markdown from "react-markdown";

import type { AutoFormFieldProps, FieldWrapperProps } from "@autoform/react";
import { ZodProvider } from "@autoform/zod";
import { ChevronDown } from "lucide-react";

import {
  AreaEnrichmentField,
  type AreaScope,
  AreaScopeContext,
} from "@/components/enrichment/area-enrichment-field";
import { JsonEnrichmentEditor } from "@/components/enrichment/json-enrichment-editor";
import { AutoForm } from "@/components/ui/autoform";
import { FieldWrapper } from "@/components/ui/autoform/components/FieldWrapper";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import FeaturesField from "@/components/ui/autoform/components/custom/FeaturesField";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import { type EnrichmentFormBundle, buildEnrichmentForm } from "./build-schema";
import type { EnrichmentPageConfig } from "./config";
import { NEVER_INLINE_KEYS, isPlainObject } from "./field-rules";

/** A no-op with a stable identity, for the read-only JSON fields. */
const noop = () => {};

/**
 * Hidden fields are `transliterations`, `translations`, the pipeline-owned keys
 * and every key the read carried that the config does not name. `HiddenField`
 * renders nothing — but AutoForm's `FieldWrapper` would still print the LABEL
 * above it, so the wrapper drops the whole field instead.
 */
function EnrichmentFieldWrapper(props: FieldWrapperProps) {
  if (props.field.type === "hidden") return null;
  return <FieldWrapper {...props} />;
}

/**
 * A `json`-control key: shown, never typed into (design C.3 — the fallback is
 * read-only). `movie.topReviews`, `venue.photos` and `genre.metadata` land here,
 * and the escape hatch for them is the page's "Raw JSON" card.
 */
function JsonReadOnlyField({ id, value }: AutoFormFieldProps) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Read-only. To change this value, use the “Raw JSON” card at the bottom
        of the page.
      </p>
      <div className="pointer-events-none">
        <JsonEnrichmentEditor
          value={{ [id]: value }}
          onChange={noop}
          onValidityChange={noop}
          height={180}
        />
      </div>
    </div>
  );
}

/**
 * Pushes every form change up to the page.
 *
 * A child of `AutoForm` rather than the editor itself, because that is where
 * react-hook-form's context is available; `useFormContext()` returns a stable
 * object, so the subscription is created once. `watch(callback)` is the
 * documented "notify on every change without re-rendering this component" API.
 */
function FormWatcher({
  onChange,
}: {
  onChange: (values: Record<string, unknown>) => void;
}) {
  const form = useFormContext();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const subscription = form.watch((values) => {
      onChangeRef.current(values as Record<string, unknown>);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return null;
}

/** A stable key set, so the schema is rebuilt only when the keys change. */
function keySignature(value: Record<string, unknown>): string {
  return Object.keys(value).sort().join(" ");
}

export interface EnrichmentEditorProps {
  config: EnrichmentPageConfig;
  enrichment: Record<string, unknown>;
  updateEnrichment: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  /** The venue's city, for the `area` picker. Only a venue has one. */
  areaScope?: AreaScope;
}

export function EnrichmentEditor({
  config,
  enrichment,
  updateEnrichment,
  disabled = false,
  areaScope,
}: EnrichmentEditorProps) {
  const [reasoningOpen, setReasoningOpen] = useState(false);

  // The schema is built from the LOADED object, not from every keystroke: its
  // field list is what AutoForm parses into form fields, and re-parsing it on
  // each character would re-render every field for no gain.
  const signature = keySignature(enrichment);
  const enrichmentRef = useRef(enrichment);
  enrichmentRef.current = enrichment;
  const bundle: EnrichmentFormBundle = useMemo(
    () => buildEnrichmentForm(config, enrichmentRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, signature],
  );
  const schemaProvider = useMemo(
    () => new ZodProvider(bundle.schema),
    [bundle],
  );

  const updateRef = useRef(updateEnrichment);
  updateRef.current = updateEnrichment;

  /**
   * Merge one form snapshot into the current draft.
   *
   * `translations` and `transliterations` are skipped: their sections own them,
   * and the form's copy is the one seeded at mount, so writing it back would
   * undo whatever the curator did in those sections.
   */
  const handleChange = useCallback((values: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...enrichmentRef.current };
    for (const [key, value] of Object.entries(values)) {
      if (NEVER_INLINE_KEYS.has(key)) continue;
      if (value === undefined) continue;
      next[key] = value;
    }
    updateRef.current(next);
  }, []);

  const components = useMemo(() => {
    const injected = injectCustomAutoFormComponents();
    return {
      uiComponents: {
        ...injected.uiComponents,
        FieldWrapper: EnrichmentFieldWrapper,
      },
      formComponents: {
        // Registered here so the editor renders on its own; a component the
        // shared injection already provides wins, so this is a default only.
        features: FeaturesField,
        enrichmentArea: AreaEnrichmentField,
        json: JsonReadOnlyField,
        ...injected.formComponents,
      },
    };
  }, []);

  const scope = useMemo(
    () => ({ ...areaScope, disabled }),
    [areaScope, disabled],
  );

  const reasoning = enrichmentRef.current.reasoning;

  return (
    <div
      className={cn("space-y-4", disabled && "pointer-events-none opacity-60")}
      aria-busy={disabled}
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Enrichment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AreaScopeContext.Provider value={scope}>
            <AutoForm
              schema={schemaProvider}
              defaultValues={enrichment}
              // Enter must not submit: there is no form submit here — the page's
              // bottom bar owns the save, and the raw values are pushed up on
              // every change instead.
              formProps={{
                onSubmit: (event: React.FormEvent) => event.preventDefault(),
              }}
              {...components}
            >
              <FormWatcher onChange={handleChange} />
            </AutoForm>
          </AreaScopeContext.Provider>
        </CardContent>
      </Card>

      {reasoning !== undefined && reasoning !== null && (
        <Collapsible open={reasoningOpen} onOpenChange={setReasoningOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>AI Reasoning</span>
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform text-muted-foreground",
                      reasoningOpen && "rotate-180",
                    )}
                  />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="text-sm whitespace-pre-line rounded-lg p-4">
                  <ReasoningBody value={reasoning} />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
}

/**
 * `reasoning` is a `{ text, confidence }` object on the entities that have it,
 * but it is pipeline-written and unmodelled, so a bare string or an unexpected
 * shape must still render rather than blank the card.
 */
function ReasoningBody({ value }: { value: unknown }) {
  if (typeof value === "string") return <Markdown>{value}</Markdown>;
  if (isPlainObject(value) && typeof value.text === "string") {
    return <Markdown>{value.text}</Markdown>;
  }
  return (
    <pre className="text-xs whitespace-pre-wrap">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default EnrichmentEditor;
